import { Injectable } from '@angular/core';
import { BehaviorSubject, defer, from, Observable } from 'rxjs';
import { finalize, last } from 'rxjs/operators';

interface QueueTask<T> {
  id: string;
  label?: string;
  task: () => Promise<T> | Observable<T>;
  subject: BehaviorSubject<'pending' | 'running' | 'completed' | 'error'>;
  resolve: (value: T | PromiseLike<T>) => void;
  reject: (reason?: unknown) => void;
}

export interface QueueOperation<TResult> {
  result$: Observable<TResult>;
  wait: () => Promise<TResult>;
}

@Injectable({ providedIn: 'root' })
export class InMemoryQueueService {
  private queues = new Map<string, QueueTask<unknown>[]>();
  private running = new Map<string, boolean>();

  queue<TResult>(args: {
    track: string;
    task: () => Promise<TResult> | Observable<TResult>;
    options?: {
      clearPending?: boolean;
      label?: string;
    };
  }): QueueOperation<TResult> {
    const { track, task, options = {} } = args;

    if (!this.queues.has(track)) this.queues.set(track, []);
    const queue = this.queues.get(track)!;

    if (options.clearPending) {
      this.clearPending(track);
    }

    let resolveFn!: (value: TResult | PromiseLike<TResult>) => void;
    let rejectFn!: (reason?: unknown) => void;
    const subject = new BehaviorSubject<'pending' | 'running' | 'completed' | 'error'>('pending');
    const promise = new Promise<TResult>((resolve, reject) => {
      resolveFn = resolve;
      rejectFn = reject;
    });

    const taskObj: QueueTask<TResult> = {
      id: Math.random().toString(36).substring(2),
      label: options.label,
      task,
      subject,
      resolve: resolveFn,
      reject: rejectFn
    };

    queue.push(taskObj);
    this.runNext(track);

    return { result$: from(promise), wait: () => promise };
  }

  private runNext(track: string) {
    if (this.running.get(track)) return;
    const queue = this.queues.get(track);
    if (!queue || queue.length === 0) return;

    const next = queue[0];
    this.running.set(track, true);
    next.subject.next('running');

    defer(() => next.task())
      .pipe(
        last(),
        finalize(() => {
          queue.shift();
          if (queue.length === 0) {
            this.queues.delete(track);
            this.running.delete(track);
          } else {
            this.running.set(track, false);
            this.runNext(track);
          }
        })
      )
      .subscribe({
        next: result => {
          next.subject.next('completed');
          next.resolve(result);
        },
        error: err => {
          next.subject.next('error');
          next.reject(err);
        }
      });
  }

  getTrackQueue(track: string): QueueTask<unknown>[] {
    return [...(this.queues.get(track) ?? [])];
  }

  hasQueuedOrRunning(track: string, label: string): boolean {
    const q = this.queues.get(track) ?? [];
    return q.some(t => t.label === label && ['pending', 'running'].includes(t.subject.value));
  }

  isTrackBusy(track: string): boolean {
    return !!this.running.get(track);
  }

  clearPending(track: string) {
    const queue = this.queues.get(track)!;
    const runningTask = queue.find(q => q.subject.value === 'running');

    queue.length = 0;

    if (runningTask) {
      queue.push(runningTask);
    }
  }

  removePendingByLabel(track: string, label: string) {
    const q = this.queues.get(track);
    if (!q) return;

    const filtered = q.filter(t => !(t.subject.value === 'pending' && t.label === label));

    q.length = 0;
    q.push(...filtered);
  }
}
